import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Stack
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import ArticleIcon from '@mui/icons-material/Article';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { listCallsForClient } from '../../services/callsApi';
import TranscriptReviewDialog from '../call/TranscriptReviewDialog';
import CallSummaryDialog from '../call/CallSummaryDialog';

function formatDuration(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function stateTone(state) {
  switch (state) {
    case 'ended':   return 'success';
    case 'active':
    case 'ringing':
    case 'pending': return 'info';
    case 'failed':  return 'error';
    default:        return 'default';
  }
}

export default function VideoCallsTab({ token, clientId }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [summarySelected, setSummarySelected] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { calls: list } = await listCallsForClient({ token, clientId });
        if (!cancelled) setCalls(list || []);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.msg || 'Could not load calls');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, clientId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  if (calls.length === 0) {
    return (
      <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
        <VideoCallIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No completed video calls with this client yet.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          A booked appointment shows above; start a call from the header at the time.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Transcript</TableCell>
              <TableCell align="right">Summary</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.map((c) => (
              <TableRow key={c._id} hover>
                <TableCell>
                  <Stack spacing={0} direction="column">
                    <Typography variant="body2">
                      {new Date(c.createdAt).toLocaleString()}
                    </Typography>
                    {c.failureReason && (
                      <Typography variant="caption" color="error">
                        {c.failureReason}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{formatDuration(c.durationSec)}</TableCell>
                <TableCell>
                  <Chip
                    label={c.state}
                    size="small"
                    color={stateTone(c.state)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<ArticleIcon />}
                    onClick={() => setSelected(c)}
                    disabled={c.state !== 'ended' && c.state !== 'failed'}
                  >
                    View
                  </Button>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    startIcon={<SummarizeIcon />}
                    onClick={() => setSummarySelected(c)}
                    disabled={c.state !== 'ended' && c.state !== 'failed'}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <TranscriptReviewDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        token={token}
        liveCallId={selected?._id}
        callMeta={selected}
      />
      <CallSummaryDialog
        open={!!summarySelected}
        onClose={() => setSummarySelected(null)}
        token={token}
        liveCallId={summarySelected?._id}
        callMeta={summarySelected}
      />
    </Box>
  );
}
